/* iceberg-ui */
import { Box } from '@auviknetworks/iceberg-ui';

/*  Shared  */
import { Notify } from 'application/Shared/Helpers';

/*  Components  */
import { debug } from 'application/Shared/Utils';
import { ReportParams } from 'application/Shared/types/reports';

/*  Hooks  */
import { useFetchReportData } from 'hooks/dataAccess/Reports/reportData';
import { useReportParams } from 'hooks/useReportParams';

/*  React  */
import { TFunction } from 'i18next';
import React, {
    Dispatch,
    SetStateAction,
    SyntheticEvent,
    useEffect,
    useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';

/* Components */
import { EditReportFlyout } from './EditReportFlyout';
import { ReportNavigationFlyout } from './ReportNavigationFlyout';
import { buildSubReportsSelection, getReportConfigByType } from './helpers';
import { ChooseReportComponent } from './internal/ChooseReportComponent';
import { ChosenReportHeaderTabs } from './internal/ChosenReportHeaderTabs';
import { ReportHasNoData } from './internal/ReportHasNoData';
import { ReportPageHeader } from './internal/ReportPageHeader';

const fetchReportData = async ({
    clientId,
    getReportData,
    reportParams,
    setNoData,
    setReportData,
    setLoadingReport,
    setShowEditFlyout,
    setSubReportType,
    t
}: {
    clientId?: string | null;
    getReportData: ({
        params,
        reportEndpoint,
        t
    }: {
        params: ReportParams;
        reportEndpoint: string;
        t: TFunction<'translation', undefined>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }) => Promise<any>;
    reportParams: ReportParams;
    setNoData: Dispatch<SetStateAction<boolean>>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setReportData: Dispatch<SetStateAction<any>>;
    setLoadingReport: Dispatch<SetStateAction<boolean>>;
    setShowEditFlyout: Dispatch<SetStateAction<boolean>>;
    setSubReportType: Dispatch<SetStateAction<string>>;
    t: TFunction<'translation', undefined>;
}) => {
    if (
        !reportParams ||
        !Object.keys(reportParams) ||
        !(Object.keys(reportParams).length > 0)
    ) {
        return;
    }

    const {
        businessOwnerSelection,
        reportSelections,
        startTime,
        type,
        userSelection
    } = reportParams;

    setLoadingReport(true);

    if (type === 'businessOwner' && businessOwnerSelection === null) {
        setLoadingReport(false);
        Notify.error(t('Reports.error_select_business_owner'));
        return;
    }

    // reportSelections come from the BE as ['discovery', 'discoveryDiscovery', 'discoveryDiscoveryApplicationBreakdown']
    // If a report contains subReports, such as the organizational one that has the usage, library and categories,
    // we need to build those subreports to be passed into the tab headers, e.g.
    const subReports = buildSubReportsSelection({
        reportSelections,
        type
    });

    try {
        setNoData(false); // Reset noData every time we run this, otherwise a previously empty report will affect it.
        const foundData: boolean[] = [];
        const reportData = {};

        const params = { ...reportParams };

        if (type === 'offboard') {
            if (
                !userSelection?.length ||
                (Array.isArray(userSelection) && userSelection[0] === '0')
            ) {
                setLoadingReport(false);
                Notify.error(t('Common.no_user_selected.error'));
                return;
            }
        }

        // For the extension code that collects the data we need for product adoption, we do the same,
        // we need to offset the start and end times by the timezone difference.
        // Can't be done in ReportBuilder or by changing reportData directly because that causes the wrong date/time to be shown to the user.
        // Therefore, let's update the dates in the params directly, then.
        // So far, this change is needed for the product adoption report. It is hard to find out if this offset is needed for another report.
        if (type === 'productAdoption') {
            // getTimezoneOffset returns the number of hours, so to obtain the milliseconds, one needs to multiply by 60 * 1000
            const timeOffset =
                new Date(startTime as number).getTimezoneOffset() * 60000;
            if (timeOffset) {
                params.startTime -= timeOffset;
                params.endTime -= timeOffset;
            }
        }

        if (clientId) {
            params.platformTenantId = clientId;
        }

        const promises = subReports.map((subR) =>
            getReportData({
                params,
                reportEndpoint: subR.reportEndpoint,
                t
            })
        );

        const results = await Promise.allSettled(promises);

        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                const {
                    error,
                    foundData: subReportFoundData,
                    reportData: subReportReportData
                } = result.value;
                // @ts-expect-error FIX ME
                reportData[subReports[index].type] = subReportReportData;
                foundData[index] = subReportFoundData;
                if (error) {
                    debug.e('Error getting report:', error);
                    setLoadingReport(false);
                }
            } else {
                // result.status === 'rejected'
                const error = result.reason;
                debug.e('Error getting report:', error);
            }
        });

        // If for all the subreports there was no found data.
        if (!foundData.includes(true)) {
            setReportData(null);
            setNoData(true);
            setShowEditFlyout(true);
        } else {
            setReportData(reportData);
            setSubReportType(subReports[0]?.type);
        }
    } catch (e) {
        debug.e(e);
    } finally {
        setLoadingReport(false);
    }
};

export const ReportsExported = () => {
    const { t } = useTranslation();
    const { clientId, reportType } = useParams();

    const [noData, setNoData] = useState(false);
    const [renderingScheduledReport, setRenderingScheduledReport] =
        useState(false);
    const [reportData, setReportData] = useState(null);
    const [loadingReport, setLoadingReport] = useState(false);
    const [showEditFlyout, setShowEditFlyout] = useState(false);

    // Get the original report config so that we can find the subReport tabs, e.g., for organizational, Usage is the first one, not Library
    const reportConfig = getReportConfigByType(reportType!);

    const [showNavigationFlyout, setShowNavigationFlyout] = useState(false);
    const [subReportType, setSubReportType] = useState(
        reportConfig?.checkboxes[0].type || ''
    );

    const [searchParams, setSearchParams] = useSearchParams();
    // scheduledReports run from the backend encode the reportParams
    const decodeReportParams = searchParams.get('decodeReportParams');

    const parsedReportParams = useReportParams(reportType);

    const { getReportData } = useFetchReportData();

    // Navigation flyout handlers
    const openNavigationFlyout = () => {
        setShowNavigationFlyout(true);
        setShowEditFlyout(false);
    };
    const openEditFlyout = () => {
        setShowEditFlyout(true);
        setShowNavigationFlyout(false);
    };

    const handleRunReport = (newReportParams: ReportParams) => {
        // Update URL with new parameters using React Router
        setSearchParams(
            (prevParams) => {
                prevParams.set('reportParams', JSON.stringify(newReportParams));
                return prevParams;
            },
            { replace: true }
        );

        // Fetch report data with new parameters directly
        fetchReportData({
            clientId,
            getReportData,
            reportParams: newReportParams,
            setLoadingReport,
            setNoData,
            setReportData,
            setShowEditFlyout,
            setSubReportType,
            t
        });
    };

    // If report comes from URL, run report with the URL params
    useEffect(() => {
        if (parsedReportParams) {
            fetchReportData({
                clientId,
                getReportData,
                reportParams: parsedReportParams,
                setLoadingReport,
                setNoData,
                setReportData,
                setShowEditFlyout,
                setSubReportType,
                t
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, getReportData, t]);

    // decodeReportParams is only true if the report is a report triggered via puppeteer in the BE
    useEffect(() => {
        if (decodeReportParams) {
            setRenderingScheduledReport(true);
        }
    }, [decodeReportParams]);

    if (!reportType) {
        return null;
    }

    const shouldRenderEditFlyout =
        showEditFlyout &&
        parsedReportParams &&
        !renderingScheduledReport &&
        !loadingReport;

    const showNoDataAlertForPuppeteerRenderedReports =
        noData && renderingScheduledReport && !loadingReport;

    return (
        <Box
            sx={{ '@media print': { padding: '0px !important' } }}
            data-trackid="reportContent"
            id="reportsExportedMainContainer"
        >
            <ReportPageHeader
                noData={noData}
                setupReportView={false}
                showActions
                openEditFlyout={openEditFlyout}
                openNavigationFlyout={openNavigationFlyout}
            />

            {showNoDataAlertForPuppeteerRenderedReports && (
                <Box
                    sx={{
                        '@media print': { padding: '24px !important' },
                        padding: 0,
                        paddingBottom: '16px'
                    }}
                >
                    <ReportHasNoData />
                </Box>
            )}

            {/* Some reports like organizational usage have multiple subReports in them, shown in different tabs  */}
            <ChosenReportHeaderTabs
                handleTabChange={(_: SyntheticEvent, value: string) =>
                    setSubReportType(value)
                }
                tab={subReportType}
            />
            {/* These are the actual reports (data) */}
            <ChooseReportComponent
                renderingScheduledReport={renderingScheduledReport}
                reportData={reportData}
                loadingReport={loadingReport}
                subReportType={subReportType}
            />

            {shouldRenderEditFlyout && (
                <EditReportFlyout
                    noData={noData}
                    onClose={() => setShowEditFlyout(false)}
                    onRunReport={handleRunReport}
                    reportParams={parsedReportParams}
                    reportType={reportType!}
                />
            )}
            {/* Navigation Flyout */}
            {showNavigationFlyout && (
                <ReportNavigationFlyout
                    onClose={() => setShowNavigationFlyout(false)}
                    currentTab={subReportType}
                    onTabChange={setSubReportType}
                />
            )}
        </Box>
    );
};
